<template>
  <modal
    ref="editmodal"
    :value="isOpen"
    variant="default"
    no-footer
    class="modify-modal modify-modal__edit"
    :aria-labelled-by="
      isStepTwo || !isMultiRoomReservation
        ? 'modify-edit-modal-title-step-two'
        : 'modify-edit-modal-title-step-one'
    "
    @input="handleOpenModal"
    @close="handleCloseModal"
  >
    <template #default="{ activate }">
      <slot v-bind="{ activate }" />
    </template>

    <template #header>
      <div class="modify-modal__edit-header">
        <div v-if="isMultiRoomReservation">
          <div class="step">
            {{ ModifyReservationPopupStepsLabel }}
          </div>
          <div
            v-if="!isStepTwo"
            id="modify-edit-modal-title-step-one"
            class="title"
          >
            {{ ModifyReservationPopupTitle }}
          </div>
        </div>
        <div v-if="isStepTwo || !isMultiRoomReservation">
          <div
            id="modify-edit-modal-title-step-two"
            class="title"
          >
            {{ ModifyReservationPopupStepTwoTitle }}
          </div>
        </div>
      </div>
    </template>
    <template #body>
      <div v-if="isMultiRoomReservation && !isStepTwo">
        <div class="modify-modal__edit-content">
          <ul class="modify-modal__room-list form">
            <li
              v-for="(item, index) in confirmedReservations"
              :key="item.id"
              class="modify-modal__room-list-item"
            >
              <label
                :for="item.id"
                :class="{ disabled: disableReservation(item) }"
              >
                <input
                  v-if="isMultiRoomReservation"
                  :id="item.id"
                  v-model="selectedRooms"
                  type="checkbox"
                  :checked="!isMultiRoomReservation"
                  :disabled="disableReservation(item)"
                  :value="item"
                >
                {{ modifyBookingPropData.ModifyReservationRoomTitle }}
                {{ item.roomNumberOrder }}: {{ item.roomStays[0].roomName }}
              </label>
              <v-dropdown
                v-if="disableReservation(item)"
                class="tooltip tooltip-variant--modify-edit-modal"
                popper-class="booking-modify-tooltip"
                placement="top"
                :distance="22"
                :skidding="0"
              >
                <button
                  class="tooltip-icon icon-info"
                  role="presentation"
                  :aria-label="getTooltipMessage(item)"
                />

                <template #popper>
                  <div
                    class="tooltip-variant--modify-edit-modal tooltip-content"
                  >
                    <span
                      v-close-popper
                      aria-label="close"
                      tabindex="0"
                      class="icon-close"
                    />
                    <div class="tooltip-body">
                      {{ getTooltipMessage(item) }}
                    </div>
                  </div>
                </template>
              </v-dropdown>
            </li>
          </ul>
          <span
            v-if="
              selectedRooms.length > 0 &&
                !!modifyBookingPropData.ModifyReservationDateWarning
            "
            class="disclaimer"
          >
            <span class="icon-warning-red" />
            {{ modifyBookingPropData.ModifyReservationDateWarning }}
          </span>
        </div>
      </div>

      <div v-if="isStepTwo || !isMultiRoomReservation">
        <div class="form modify-modal__edit-content -editList">
          <!--start Rooms and Hotel Radio-->
          <label>
            <input
              id="modifyReservationInfo-0"
              v-model="editOptionSelected"
              type="radio"
              name="modifyReservationInfo"
              value="room-list"
            >
            {{ ModifyReservationPopupStepTwoRoomServices }}
          </label>
          <span
            v-if="editOptionSelected === 'room-list'"
            class="disclaimer"
          >
            <span class="icon-warning-red" />
            {{ ModifyReservationPopupStepTwoRoomServicesAlert }}
          </span>
          <!--end Rooms and Hotel Radio-->

          <!--start Privileges Radio-->
          <label v-if="hasEnhancements">
            <input
              id="modifyReservationInfo-1"
              v-model="editOptionSelected"
              type="radio"
              name="modifyReservationInfo"
              :value="isAuthenticated ? 'enhancements' : 'join-fans'"
            >
            {{ ModifyReservationPopupStepTwoCompPrivilege }}
          </label>
          <!--end Privileges Radio-->

          <!--start Guest Payment Radio-->
          <label>
            <input
              id="modifyReservationInfo-2"
              v-model="editOptionSelected"
              type="radio"
              name="modifyReservationInfo"
              value="check-out"
            >
            {{ ModifyReservationPopupStepTwoGuestInfo }}
          </label>
          <!--end Guest Payment Radio-->
        </div>
      </div>

      <div class="button-wrapper form-submit">
        <div v-if="isMultiRoomReservation && !isStepTwo">
          <button
            class="button--primary black size-md"
            :disabled="nextCtaDisabled"
            :class="nextCtaDisabled ? 'disabled' : ''"
            @click="next"
            @keydown.space.prevent="next"
            @keydown.enter.prevent="next"
          >
            {{ ModifyReservationPopupStepOneCTANextLabel }}
          </button>
        </div>
        <div v-if="isStepTwo || !isMultiRoomReservation">
          <button
            v-if="isMultiRoomReservation"
            class="button--secondary black size-md outlined"
            @click="goBack"
            @keydown.space.prevent="goBack"
            @keydown.enter.prevent="goBack"
          >
            {{ ModifyReservationPopupBackButtonLabel }}
          </button>
          <button
            class="button--primary black size-md"
            :disabled="!editOptionSelected || isLoading"
            :class="{ disabled: !editOptionSelected, loading: isLoading }"
            @click="edit"
            @keydown.space.prevent="edit"
            @keydown.enter.prevent="edit"
          >
            {{ ModifyReservationPopupStepOneCTAEditLabel }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
  import {
    mapActions, mapGetters, mapMutations, mapState
  } from 'vuex'
  import { logger } from '@plugins/logger'
  import axios from 'axios'
  import { getApiUrl } from '@utils/api-helpers'
  import { formatDateZone } from '@utils/date-helpers'

  export default {
    name: 'ModifyEditModal',
    inject: ['bookingUrl'],
    data: () => ({
      isOpen: false,
      isStepTwo: false,
      currentStep: 1,
      numOfSteps: 2,
      nextCtaDisabled: true, // next button disable if no room is selected
      showDisclaimer: false, // rooms radio button show/hide disclaimer,
      editOptionSelected: null, // edit option selected
      isLoading: false,
      localQuery: {},
      hasEnhancements: true,
      bookingEditModeSteps: {
        'room-list': '/',
        'join-fans': '/join-fans',
        enhancements: '/enhancements',
        'check-out': '/check-out',
      },
    }),
    watch: {
      isOpen: {
        immediate: true,
        handler(val) {
          if (val) {
            this.$track(
              this.getBookingTrackingModifyReservation(
                undefined,
                {
                  event: 'bookingStateChange',
                  bookingEngine: {
                    bookingStepNumber: '0.2',
                    bookingStepName: this.isMultiRoomReservation && !this.isStepTwo ? 'Room Selection Modal' : 'Modify Reservation Modal',
                    bookingEngineVersion: '2',
                    bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
                  }
                }
              )
            )
            
            if (window.adobe?.target?.triggerView) {
              window.adobe.target.triggerView(this.isMultiRoomReservation && !this.isStepTwo ? 'Room Selection Modal' : 'Modify Reservation Modal')
            }
          }
        }
      },
      isStepTwo(val) {
        if (val) {
          this.$track(
            this.getBookingTrackingModifyReservation(
              undefined,
              {
                event: 'bookingStateChange',
                bookingEngine: {
                  bookingStepNumber: '0.3',
                  bookingStepName: 'Modify Reservation Modal',
                  bookingEngineVersion: '2',
                  bookingFlow: 'multiRoomMod'
                }
              },
              true
            )
          )
        }
      }
    },
    async mounted() {
      this.selectedRooms = this.selectedReservations
      if (!this.isMultiRoomReservation && this.currentReservationData?.reservations?.[0]) {
        await this.validateEnhancements([this.currentReservationData.reservations[0]])
      }
    },
    computed: {
      ...mapGetters('auth', ['isAuthenticated', 'fanLevel']),
      ...mapState('global', ['lang']),
      ...mapState('modifyReservation', [
        'currentReservationData',
        'modifyBookingPropData',
        'selectedReservations',
      ]),
      ...mapGetters('modifyReservation', [
        'isMultiRoomReservation',
        'getBookingTrackingModifyReservation',
        'reservationGuestProfile'
      ]),
      ...mapGetters('cart', ['itemsInCart']),
      selectedRooms: {
        get() {
          return this.selectedReservations
        },
        set(value) {
          if (value.length === 0) {
            this.nextCtaDisabled = true
          } else {
            this.nextCtaDisabled = false
          }
          this.updatedSelectedReservations(value)
        },
      },
      confirmedReservations() {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        return this.currentReservationData.reservations.filter((item) => {
          if (item.status !== 'Committed') {
            return false
          }
          const startDate = new Date(item.roomStays[0].startDate)
          startDate.setHours(0, 0, 0, 0)
          return startDate >= now
        })
      },
      ModifyReservationPopupStepsLabel() {
        let label
        label = this.modifyBookingPropData?.ModifyReservationStepPopupStepOfLabel.slice(
          0,
          -1
        ) + this.numOfSteps
        const hastagCurrent = '#'
        let newLabel
        newLabel = label.replace(hastagCurrent, this.currentStep)

        return this.modifyBookingPropData?.ModifyReservationStepPopupStepOfLabel
          ? newLabel
          : ''
      },
      ModifyReservationPopupTitle() {
        return this.modifyBookingPropData?.ModifyReservationStepOnePopupTitle
          ? this.modifyBookingPropData.ModifyReservationStepOnePopupTitle
          : ''
      },
      ModifyReservationPopupStepTwoTitle() {
        return this.modifyBookingPropData?.ModifyReservationStepTwoPopupTitle
          ? this.modifyBookingPropData.ModifyReservationStepTwoPopupTitle
          : ''
      },
      ModifyReservationPopupStepTwoRoomServices() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepTwoPopupRoomServicesLabel
          ? this.modifyBookingPropData
            .ModifyReservationStepTwoPopupRoomServicesLabel
          : ''
      },
      ModifyReservationPopupStepTwoCompPrivilege() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepTwoPopupEnhancementsLabel
          ? this.modifyBookingPropData
            .ModifyReservationStepTwoPopupEnhancementsLabel
          : ''
      },
      ModifyReservationPopupStepTwoGuestInfo() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepTwoPopupGuestInfoLabel
          ? this.modifyBookingPropData.ModifyReservationStepTwoPopupGuestInfoLabel
          : ''
      },
      ModifyReservationPopupStepTwoRoomServicesAlert() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepTwoPopupRoomServicesAlertLabel
          ? this.modifyBookingPropData
            .ModifyReservationStepTwoPopupRoomServicesAlertLabel
          : ''
      },
      ModifyReservationPopupStepOneCTANextLabel() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepOnePopupCTANextLabel
          ? this.modifyBookingPropData.ModifyReservationStepOnePopupCTANextLabel
          : ''
      },
      ModifyReservationPopupStepOneCTAEditLabel() {
        return this.modifyBookingPropData
          ?.ModifyReservationStepTwoPopupCTAEditLabel
          ? this.modifyBookingPropData.ModifyReservationStepTwoPopupCTAEditLabel
          : ''
      },
      ModifyReservationPopupBackButtonLabel() {
        return this.modifyBookingPropData?.ModifyReservationPopupBackButtonLabel
          ? this.modifyBookingPropData.ModifyReservationPopupBackButtonLabel
          : ''
      },
    },
    methods: {
      ...mapActions('modifyReservation', [
        'editReservation',
        'updatedSelectedReservations',
        'toggleBookingEditMode',
      ]),
      ...mapActions('booking', ['updateQuery']),
      ...mapActions('cart', {
        resetCartAction: 'resetCart',
        addToCartAction: 'addToCart',
      }),
      ...mapMutations('cart', ['UPDATE_CART']),

      deactivate() {
        this.$emit('deactivate')
      },
      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.deactivate()
        this.currentStep = 1
        this.isStepTwo = false
        this.nextCtaDisabled = true
        this.editOptionSelected = null
        this.updatedSelectedReservations([])
      },
      handleOpenModal() {
        document.body.classList.add('no-scroll')
        if (!this.isMultiRoomReservation) {
          // If there's only one reservation, update the selectedReservation
          this.updatedSelectedReservations([
            this.currentReservationData.reservations[0],
          ])
        }
      },
      async next() {
        await this.validateEnhancements(this.selectedRooms)
        this.isStepTwo = true
        this.currentStep = 2
      },
      goBack() {
        this.isStepTwo = false
        this.currentStep = 1
      },
      async edit() {
        this.isLoading = true
        this.toggleBookingEditMode(true)
        await this.updateLocalQueryWithSelectedReservations()
        await this.addSelectedReservationsToCart()
        await this.addEnhancementsFromSelectedReservations()
        this.redirectUserToBookingWizard()
      },
      redirectUserToBookingWizard() {
        setTimeout(() => {
          const queryParams = Object.keys(this.localQuery)
            .map((key) => {
              if (Array.isArray(this.localQuery[key])) {
                return `${key}=${JSON.stringify(this.localQuery[key])}`
              }
              return `${key}=${this.localQuery[key]}`
            })
            .join('&')
          const bookingEditModeStep = this.bookingEditModeSteps[this.editOptionSelected]
          window.location.href = `/${this.lang}${
            this.bookingUrl
          }/#/booking${bookingEditModeStep}?${queryParams}`
        }, 500)
      },
      async updateLocalQueryWithSelectedReservations() {
        const additionalGuests = this.selectedReservations.length > 1
          ? this.selectedReservations.slice(1).map((reservation) => ({
            adultGuestCount: reservation?.adultsCount,
            childGuestCount: reservation?.childCount,
            hotelCode: reservation?.hotelCode,
          }))
          : []

        this.localQuery = {
          hotelCode: this.selectedReservations[0]?.hotelCode,
          adultGuestCount: this.selectedReservations[0]?.adultsCount,
          childGuestCount: this.selectedReservations[0]?.childCount,
          roomQuantity: this.selectedReservations?.length,
          aditionalGuests: additionalGuests,
          stayDateStart: this.selectedReservations[0]?.roomStays[0]?.startDate,
          stayDateEnd: this.selectedReservations[0]?.roomStays[0]?.endDate,
          iataNumber: null,
          groupCode: null,
          promotionCode:
            this.selectedReservations[0]?.roomStays[0]?.ratePlans[0]?.ratePlanID,
          jumpMonth: null,
          lang: this.lang,
          bookingEditMode: true,
          step: Object.keys(this.bookingEditModeSteps).indexOf(
            this.editOptionSelected
          ),
        }

        await this.updateQuery({ query: this.localQuery })
      },
      async addSelectedReservationsToCart() {
        await this.resetCartAction()
        const itemsToAdd = this.getCartItems()

        for (const item of itemsToAdd) {
          try {
            await this.addToCartAction({ item, skipValidateRoute: true })
          } catch (err) {
            this.isLoading = false
            this.$logger.error(err)
          }
        }
      },
      getCartItems() {
        return this.selectedReservations.map((reservation) => ({
          ...this.localQuery,
          addOns: reservation?.addOns,
          ratePlanCode: reservation?.roomRateCodePlanSynXis,
          roomTypeCode: reservation?.roomStays[0]?.roomTypes[0]?.roomTypeCode,
          bedTypeCode: reservation?.roomStays[0]?.roomTypes[0]?.roomTypeCode,
          nightlyRates: reservation?.prices?.nightlyRates,
          sitecoreContent: {
            bed: {
              title: reservation?.bedTypeName,
            },
            rate: {
              title: reservation?.ratePlanNameSynXis,
            },
            room: {
              title: reservation?.roomStays[0]?.roomTypes[0]?.name,
              image: reservation?.roomStays[0]?.roomTypes[0]?.images[0]?.src,
            },
          },
          total: reservation?.prices?.total,
          fees: 0,
          taxes: reservation?.prices?.taxesFees?.reduce(
            (total, currentItem) => total + currentItem.price,
            0
          ),
          taxesFees: reservation?.prices?.taxesFees,
          restrictionPolicy: `${reservation?.cancelPenaltiesText} <br> ${reservation?.roomStays[0]?.ratePlans[0]?.guarantees[0]}`,
          confirmationNumber: reservation?.id,
          enhancements: reservation?.enhancementServices,
          roomNumber: reservation?.roomNumberOrder,
        }))
      },
      async addEnhancementsFromSelectedReservations() {
        const cartLines = this.itemsInCart
        const data = cartLines.map((item, index) => ({
          lineId: item.id,
          enhancements: [
            ...this.transformEnhancements(this.selectedReservations[index].enhancementServices ?? []),
          ],
          addOns: [
            ...this.transformAddOns(this.selectedReservations[index].addOns ?? []),
          ]
        }))
        try {
          const { data: cart } = await axios.put(
            getApiUrl('/api/v1/cart/add-enhancement'),
            data
          )
          this.UPDATE_CART(cart)
        } catch (err) {
          logger.error(err)
        }
      },
      checkIfDateDiffers(item) {
        return (
          this.selectedRooms.length > 0
          && this.selectedRooms[0]?.roomStays[0]?.startDate
            !== item?.roomStays[0]?.startDate
          && this.selectedRooms[0]?.roomStays[0]?.endDate
            !== item?.roomStays[0]?.endDate
        )
      },
      disableReservation(item) {
        return !item.isModifiable || this.checkIfDateDiffers(item)
      },
      getTooltipMessage(item) {
        return !item.isModifiable
          ? this.modifyBookingPropData.ModifyReservationNotModifyTooltip
          : this.modifyBookingPropData.ModifyReservationSameDateEditRule
      },
      transformAddOns(addOns) {
        return addOns.map((addOn) => {
          const totalSelectedQuantity = addOn.excludeKidsFromPriceCalculations
            ? parseInt(addOn.adultQuantity, 10)
            : parseInt(addOn.adultQuantity, 10) + parseInt(addOn.childQuantity, 10)
          return {
            data: {},
            addOnName: addOn.addOnName,
            addOnDescription: null,
            code: addOn.synxisId,
            quantity: totalSelectedQuantity,
            adultQuantity: parseInt(addOn.adultQuantity, 10),
            childQuantity: parseInt(addOn.childQuantity, 10),
            price: [addOn.priceBeforeTax],
            priceWithTaxes: [addOn.priceWithTax],
            total: addOn.totalPrice[0].toString(),
            totalTax: addOn.taxes.totalTaxPricePaidForAddOnByUser.toString(),
            synXisId: addOn.synxisId,
            sitecoreId: addOn.airportAddOn ? addOn.sitecoreParentId : addOn.sitecoreId,
            servicePricingType: addOn.servicePricingTypeKey,
            airportAddOn: addOn.airportAddOn,
            excludeKidsFromPriceCalculations: addOn.excludeKidsFromPriceCalculations,
            addOnTax: {
              [formatDateZone(addOn.timeSpan[0], null, 'YYYY-MM-DD')]: addOn.taxes.taxLine.map((tax) => ({
                taxLines: tax.taxLineDescription,
                price: tax.taxLinePrice.toString(),
                code: tax.code,
              })),
            },
            filledData: false
          }
        })
      },
      transformEnhancements(enhancements) {
        return enhancements.map((enhancement) => ({
          sitecoreId: enhancement.sitecoreId,
          synXisId: enhancement.synXisId,
          description: enhancement.title,
          data: null
        }))
      },
      async validateEnhancements(reservations) {
        try {
          const requests = reservations.map((item) => ({
            HotelCode: item.hotelCode,
            AdultGuestCount: item?.adultsCount,
            ChildGuestCount: item?.childCount,
            Start: item.roomStays[0].startDate,
            End: item.roomStays[0].endDate,
            RateCode: item.roomRateCodePlanSynXis
          }))

          const { fanLevel } = this
          const privilegesFromSitecoreMap = this.currentReservationData.property?.enhancements
          const addOnsFromSitecoreMap = this.currentReservationData.property?.addOns
          const filteredComplimentaryPrivileges = []
          const filteredAddOns = []

          const validateEnhancementsResponse = await Promise.all(requests.map(async (request, index) => {
            try {
              const { data } = await axios.post(getApiUrl('/api/v1/booking/validate-enhancements'), request)
              return {
                roomIndex: index,
                enhancements: data?.enhancements || []
              }
            } catch (error) {
              logger.error(`Error fetching enhancements for item at index ${index}:`, error)
              return { roomIndex: index, enhancements: [] }
            }
          }))

          const { enhancements } = validateEnhancementsResponse[0]

          if (fanLevel && enhancements.length > 0) {
            enhancements.forEach((enhancement) => {
              const privilege = privilegesFromSitecoreMap.find((item) => item.synxisId === enhancement.code)
              const addOn = addOnsFromSitecoreMap.find((item) => item.synxisId === enhancement.code)

              if (privilege && privilege.fanLevel === fanLevel) {
                filteredComplimentaryPrivileges.push({ ...enhancement })
              }

              if (addOn) {
                filteredAddOns.push({ ...enhancement })
              }
            })

            this.hasEnhancements = filteredComplimentaryPrivileges.length > 0 || filteredAddOns.length > 0
          } else {
            this.hasEnhancements = validateEnhancementsResponse.some((response) => response.enhancements.length > 0)
          }
        } catch (err) {
          logger.error(err)
        }
      }
    },
  }
</script>
